import React, { useCallback } from 'react'
import { Button, Columns, Element, Form, Heading, Icon } from 'react-bulma-components'
import {
  MissionScheduleTemplate,
  MissionScheduleWeekPattern,
  MissionScheduleDaySlot,
} from '../../api/missions'
import {
  formatISODate,
  hoursAsNumberToTimeInputValue,
  timeInputValueToHoursAsNumber,
} from '../../utils/date'
import { ClipboardProvider, useClipboardContext } from '../../hooks/use-clipboard'
import { RemoveIcon } from '../icons'
import { FormFieldErrorMessage } from '../form/fields/form-field'
import TimePicker from '../form/fields/time-picker'
import { Day } from '../schedule/day'
import { DayCellActions } from '../schedule/day-cell-actions'
import { Week } from '../schedule/week'
import { WeeksList } from '../schedule/weeks-list'
import { BreakRule } from '../../../../backend/src/services/resources/clocking-rules/clocking-rule.model'
import { calculateScheduleTemplateWeekDuration } from '../../utils/work-periods'
import { useExpectedWeeklyHours } from '../../hooks/use-work-period-duration'
import { WeekDurationDisplay } from './mission-week-duration'
import { User } from '../../api/users'

/** Type guard for pasted clipboard value (day slot). */
function isMissionScheduleDaySlot(v: unknown): v is MissionScheduleDaySlot {
  return (
    v != null &&
    typeof (v as MissionScheduleDaySlot).startHour === 'number' &&
    typeof (v as MissionScheduleDaySlot).endHour === 'number'
  )
}

/** Day labels (0 = Monday … 6 = Sunday). */
const DAY_LABELS: Record<0 | 1 | 2 | 3 | 4 | 5 | 6, string> = {
  0: 'Lundi',
  1: 'Mardi',
  2: 'Mercredi',
  3: 'Jeudi',
  4: 'Vendredi',
  5: 'Samedi',
  6: 'Dimanche',
}

const DAY_KEYS: (0 | 1 | 2 | 3 | 4 | 5 | 6)[] = [0, 1, 2, 3, 4, 5, 6]

/**
 * Displays total hours per week for a schedule template week pattern (with break rule applied).
 * When userId and referenceDate are provided, shows expected from contract and status icon (same as work-periods editor).
 */
const ScheduleTemplateWeekDuration: React.FC<{
  weekPattern: MissionScheduleWeekPattern
  breakRule?: BreakRule
  userId?: User['_id']
  referenceDate?: Date
}> = ({ weekPattern, breakRule, userId, referenceDate }) => {
  const total = calculateScheduleTemplateWeekDuration(weekPattern, breakRule)
  const expected = useExpectedWeeklyHours(userId, referenceDate)
  return <WeekDurationDisplay total={total} expected={expected} />
}

/**
 * Creates a default schedule template: start = today, end = null (no end date),
 * one week pattern with Mon–Fri 9h–17h.
 */
export function createDefaultScheduleTemplate(): MissionScheduleTemplate {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const days: Partial<Record<0 | 1 | 2 | 3 | 4 | 5 | 6, MissionScheduleDaySlot>> = {}

  return {
    start: today,
    end: null,
    weekPatterns: [{ days }],
  }
}

export interface ScheduleTemplateEditorProps {
  /** The single schedule template to edit. */
  scheduleTemplate: MissionScheduleTemplate
  /** Callback to update the schedule template. */
  setScheduleTemplate: (template: MissionScheduleTemplate) => void
  /** Validation error string to display, if any. */
  errors?: string
  /** Whether the editor is disabled (read-only mode). */
  disabled?: boolean
  /** Break rule from job title clocking rules; used to display hours per week with break deduction. */
  breakRule?: BreakRule
  /** Worker user id; when set, expected weekly hours and status icon are shown (same as work-periods editor). */
  userId?: User['_id']
}

/** Props for the table that uses clipboard (must be under ClipboardProvider). */
interface ScheduleTemplateTableProps {
  scheduleTemplate: MissionScheduleTemplate
  setDaySlot: (
    patternIndex: number,
    day: 0 | 1 | 2 | 3 | 4 | 5 | 6,
    slot: MissionScheduleDaySlot | null,
  ) => void
  removeWeekPattern: (patternIndex: number) => void
  addWeekPattern: () => void
  disabled: boolean
  breakRule?: BreakRule
  userId?: User['_id']
}

/**
 * Table of week patterns with copy/paste per day cell.
 * Uses useClipboardContext<MissionScheduleDaySlot>; must be rendered inside ClipboardProvider.
 */
const ScheduleTemplateTable: React.FunctionComponent<ScheduleTemplateTableProps> = ({
  scheduleTemplate,
  setDaySlot,
  removeWeekPattern,
  addWeekPattern,
  disabled,
  breakRule,
  userId,
}) => {
  const clipboard = useClipboardContext<MissionScheduleDaySlot>()
  const referenceDate = scheduleTemplate.start ? new Date(scheduleTemplate.start) : undefined

  return (
    <>
      <WeeksList>
        <WeeksList.Header
          left={wD => DAY_LABELS[(wD === 0 ? 6 : (wD as number) - 1) as 0 | 1 | 2 | 3 | 4 | 5 | 6]}
        />
        {scheduleTemplate.weekPatterns.map((pattern, patternIndex) => (
          <Week key={patternIndex} week={patternIndex} weekYear={0}>
            <Week.Header
              week={patternIndex}
              weekYear={0}
              left={`S${patternIndex}`}
              right={
                <>
                  <Element textSize={7} mt={1}>
                    <ScheduleTemplateWeekDuration
                      weekPattern={pattern}
                      breakRule={breakRule}
                      userId={userId}
                      referenceDate={referenceDate}
                    />
                  </Element>
                </>
              }
            >
              <Element mt={3}>
                <Button
                  size="small"
                  color="danger"
                  onClick={() => removeWeekPattern(patternIndex)}
                  disabled={disabled || scheduleTemplate.weekPatterns.length <= 1}
                >
                  <Icon>
                    <RemoveIcon />
                  </Icon>
                </Button>
              </Element>
            </Week.Header>
            {DAY_KEYS.map(day => {
              const slot = pattern.days[day]
              const startValue =
                slot && slot.startHour ? hoursAsNumberToTimeInputValue(slot.startHour) : ''
              const endValue =
                slot && slot.endHour ? hoursAsNumberToTimeInputValue(slot.endHour) : ''

              return (
                <Day
                  key={day}
                  right={
                    <DayCellActions
                      onCopy={() => {
                        if (slot) clipboard.copy(slot)
                      }}
                      onPaste={() => {
                        if (!clipboard.value || !isMissionScheduleDaySlot(clipboard.value)) return
                        setDaySlot(patternIndex, day, clipboard.value)
                      }}
                      onRemove={slot ? () => setDaySlot(patternIndex, day, null) : undefined}
                      copyDisabled={!slot || disabled || clipboard.isEqualTo(slot)}
                      pasteDisabled={
                        clipboard.isEmpty ||
                        (slot && clipboard.isEqualTo(slot)) ||
                        !isMissionScheduleDaySlot(clipboard.value)
                      }
                      removeDisabled={!slot || disabled}
                      dataTestPrefix={`s${patternIndex}-d${day}`}
                    />
                  }
                >
                  <Element display="flex" flexDirection="column" style={{ gap: '0.5rem' }}>
                    <TimePicker
                      value={startValue}
                      onChange={value => {
                        const h = timeInputValueToHoursAsNumber(value)
                        if (h !== undefined) {
                          setDaySlot(patternIndex, day, {
                            startHour: h,
                            endHour: slot?.endHour ?? undefined,
                          })
                        } else {
                          setDaySlot(patternIndex, day, null)
                        }
                      }}
                      disabled={disabled}
                      dataTest={`schedule-template-s${patternIndex}-d${day}-start`}
                    />
                    <TimePicker
                      value={endValue}
                      onChange={value => {
                        const h = timeInputValueToHoursAsNumber(value)
                        if (h !== undefined) {
                          setDaySlot(patternIndex, day, {
                            startHour: slot?.startHour ?? undefined,
                            endHour: h,
                          })
                        } else {
                          setDaySlot(patternIndex, day, null)
                        }
                      }}
                      disabled={disabled}
                      dataTest={`schedule-template-s${patternIndex}-d${day}-end`}
                    />
                  </Element>
                </Day>
              )
            })}
          </Week>
        ))}
      </WeeksList>
      <Button
        size="small"
        type="button"
        onClick={addWeekPattern}
        disabled={disabled}
        className="mt-3"
      >
        Ajouter une semaine (alternance, ex. quinzaine)
      </Button>
    </>
  )
}

/**
 * Editor for a single mission schedule template (recurring mode).
 * Allows setting start date (required), optional end date, and one or more week patterns.
 * Each week pattern defines which days (0=Mon..6=Sun) are worked and start/end hours (0–24).
 */
export const ScheduleTemplateEditor: React.FunctionComponent<ScheduleTemplateEditorProps> = ({
  scheduleTemplate,
  setScheduleTemplate,
  errors,
  disabled = false,
  breakRule,
  userId,
}) => {
  /** Patches top-level template fields (start, end, weekPatterns). */
  const updateTemplate = useCallback(
    (patch: Partial<MissionScheduleTemplate>) => {
      setScheduleTemplate({ ...scheduleTemplate, ...patch })
    },
    [scheduleTemplate, setScheduleTemplate],
  )

  /** Updates a specific week pattern inside the template. */
  const updateWeekPattern = useCallback(
    (patternIndex: number, patch: Partial<MissionScheduleWeekPattern>) => {
      const patterns = [...scheduleTemplate.weekPatterns]
      const current = patterns[patternIndex]
      patterns[patternIndex] = { days: {}, ...current, ...patch }
      updateTemplate({ weekPatterns: patterns })
    },
    [scheduleTemplate, updateTemplate],
  )

  /** Sets or removes a day slot in a specific week pattern. */
  const setDaySlot = useCallback(
    (patternIndex: number, day: 0 | 1 | 2 | 3 | 4 | 5 | 6, slot: MissionScheduleDaySlot | null) => {
      const pattern = scheduleTemplate.weekPatterns[patternIndex]
      if (!pattern) return
      const days = { ...pattern.days }
      if (slot) days[day] = slot
      else delete days[day]
      updateWeekPattern(patternIndex, { days })
    },
    [scheduleTemplate, updateWeekPattern],
  )

  /** Adds an empty week pattern to the template. */
  const addWeekPattern = useCallback(() => {
    updateTemplate({ weekPatterns: [...scheduleTemplate.weekPatterns, { days: {} }] })
  }, [scheduleTemplate, updateTemplate])

  /** Removes a week pattern by index (minimum 1 pattern must remain). */
  const removeWeekPattern = useCallback(
    (patternIndex: number) => {
      if (scheduleTemplate.weekPatterns.length <= 1) return
      updateTemplate({
        weekPatterns: scheduleTemplate.weekPatterns.filter((_, i) => i !== patternIndex),
      })
    },
    [scheduleTemplate, updateTemplate],
  )

  return (
    <>
      {errors && (
        <Element mb={3}>
          <FormFieldErrorMessage error={errors} />
        </Element>
      )}
      {/* ---------- Start / End dates ---------- */}
      <Columns>
        <Columns.Column size={6}>
          <Form.Field>
            <Form.Label>À partir du</Form.Label>
            <Form.Input
              type="date"
              value={formatISODate(scheduleTemplate.start)}
              onChange={e => {
                const v = e.target.value
                if (!v) return
                const d = new Date(v)
                d.setHours(0, 0, 0, 0)
                updateTemplate({ start: d })
              }}
              disabled={disabled}
              required
              data-test="schedule-template-start"
            />
          </Form.Field>
        </Columns.Column>
        <Columns.Column size={6}>
          <Form.Field>
            <Form.Label>
              <input
                type="checkbox"
                checked={scheduleTemplate.end === null}
                onChange={e => {
                  updateTemplate({ end: e.target.checked ? null : new Date() })
                }}
                disabled={disabled}
              />{' '}
              Pas de date de fin (mission sans échéance)
            </Form.Label>
            {scheduleTemplate.end !== null && (
              <Form.Input
                type="date"
                value={formatISODate(scheduleTemplate.end)}
                onChange={e => {
                  const v = e.target.value
                  if (!v) return
                  const d = new Date(v)
                  d.setHours(23, 59, 59, 999)
                  updateTemplate({ end: d })
                }}
                disabled={disabled}
              />
            )}
          </Form.Field>
        </Columns.Column>
      </Columns>

      {/* ---------- Week patterns: single table, one row per week, copy/paste per cell ---------- */}
      <Heading size={5} renderAs="h3" className="mt-4 mb-3">
        Masque à utiliser
      </Heading>
      <ClipboardProvider<MissionScheduleDaySlot>>
        <ScheduleTemplateTable
          scheduleTemplate={scheduleTemplate}
          setDaySlot={setDaySlot}
          removeWeekPattern={removeWeekPattern}
          addWeekPattern={addWeekPattern}
          disabled={disabled}
          breakRule={breakRule}
          userId={userId}
        />
      </ClipboardProvider>
    </>
  )
}
